/** Default reminder window when API stores nulls (14 days before due). */
export const DEFAULT_REMINDER_LEAD_DAYS = 14;

export type ReminderLeadUnit = 'days' | 'hours' | 'minutes' | 'seconds';

function normalizeReminderUnit(u: string | null | undefined): ReminderLeadUnit {
  if (u === 'days' || u === 'hours' || u === 'minutes' || u === 'seconds') {
    return u;
  }
  return 'days';
}

export function leadDurationMs(
  value: number,
  unit: string | null | undefined,
): number {
  const u = normalizeReminderUnit(unit);
  switch (u) {
    case 'days':
      return value * 86400000;
    case 'hours':
      return value * 3600000;
    case 'minutes':
      return value * 60000;
    case 'seconds':
      return value * 1000;
    default:
      return value * 86400000;
  }
}

export function effectiveReminderLead(
  reminderLeadValue: number | null | undefined,
  reminderLeadUnit: string | null | undefined,
): { value: number; unit: ReminderLeadUnit } {
  return {
    value: reminderLeadValue ?? DEFAULT_REMINDER_LEAD_DAYS,
    unit: normalizeReminderUnit(reminderLeadUnit),
  };
}

export interface DueIndicatorResult {
  visible: boolean;
  text: string;
  variant: 'upcoming' | 'soon' | 'overdue';
}

function formatTimeUntil(diffMs: number): string {
  const sec = Math.floor(diffMs / 1000);
  const days = Math.floor(sec / 86400);
  if (days >= 1) {
    return `Due in ${days} day${days === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(sec / 3600);
  if (hours >= 1) {
    return `Due in ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const minutes = Math.floor(sec / 60);
  if (minutes >= 1) {
    return `Due in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const seconds = Math.max(0, sec);
  return `Due in ${seconds} second${seconds === 1 ? '' : 's'}`;
}

function formatOverdue(overdueMs: number): string {
  const sec = Math.floor(overdueMs / 1000);
  const days = Math.floor(sec / 86400);
  if (days >= 1) {
    return `Overdue by ${days} day${days === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(sec / 3600);
  if (hours >= 1) {
    return `Overdue by ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const minutes = Math.floor(sec / 60);
  if (minutes >= 1) {
    return `Overdue by ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const seconds = Math.max(0, sec);
  return `Overdue by ${seconds} second${seconds === 1 ? '' : 's'}`;
}

/**
 * List indicator: show only inside the reminder window before due, or when overdue.
 */
export function getDueDateIndicator(
  now: Date,
  dueAtIso: string,
  reminderLeadValue?: number | null,
  reminderLeadUnit?: string | null,
): DueIndicatorResult | null {
  const due = new Date(dueAtIso);
  if (Number.isNaN(due.getTime())) return null;

  const { value, unit } = effectiveReminderLead(
    reminderLeadValue,
    reminderLeadUnit,
  );
  const leadMs = leadDurationMs(value, unit);
  const diffMs = due.getTime() - now.getTime();

  if (diffMs < 0) {
    return {
      visible: true,
      text: formatOverdue(-diffMs),
      variant: 'overdue',
    };
  }

  if (diffMs > leadMs) {
    return { visible: false, text: '', variant: 'upcoming' };
  }

  const text = formatTimeUntil(diffMs);
  return {
    visible: true,
    text,
    variant: diffMs < 86400000 ? 'soon' : 'upcoming',
  };
}

export function dueAtToDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function datetimeLocalToIso(local: string): string {
  const d = new Date(local);
  return d.toISOString();
}

/**
 * Human-readable due instant for card previews and modal (locale-aware).
 * Example: "Apr 14, 2026, 3:00 PM"
 */
export function formatCardDueAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function formatClockTime(d: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isNextCalendarMonth(now: Date, due: Date): boolean {
  const nextMonth = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
  const nextYear =
    now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  return due.getMonth() === nextMonth && due.getFullYear() === nextYear;
}

/**
 * Human-friendly due label:
 * - near term: "Due today/tomorrow/in X days", "Due next month", "Due in X weeks"
 * - far term: "Due on <absolute date>"
 */
export function formatIntuitiveDueAt(
  iso: string,
  now: Date = new Date(),
): string {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '';

  const diffMs = due.getTime() - now.getTime();
  if (diffMs < 0) return formatOverdue(-diffMs);

  const dayMs = 86400000;
  const dayDiff = Math.round(
    (startOfDay(due).getTime() - startOfDay(now).getTime()) / dayMs,
  );

  if (dayDiff <= 0) {
    return `Due today at ${formatClockTime(due)}`;
  }
  if (dayDiff === 1) {
    return `Due tomorrow at ${formatClockTime(due)}`;
  }
  if (dayDiff <= 21) {
    return `Due in ${dayDiff} day${dayDiff === 1 ? '' : 's'}`;
  }
  if (isNextCalendarMonth(now, due) && dayDiff <= 62) {
    return 'Due next month';
  }
  if (dayDiff <= 90) {
    const weeks = Math.ceil(dayDiff / 7);
    return `Due in ${weeks} week${weeks === 1 ? '' : 's'}`;
  }

  return `Due on ${formatCardDueAt(iso)}`;
}
